// Moved from the W0 spike; ports SuiteModel/TextOffsets.swift.
// Unicode-scalar ↔ UTF-16 offsets. The Swift app stores text positions in scalars (comment anchors, LeftOff,
// Handoff carets); JavaScript strings and JS Automerge's text API count UTF-16 code units. They differ only at
// surrogate pairs. Behaviour mirrors SuiteModel/TextOffsets.swift, clamping included.

const isLead = (u: number) => u >= 0xd800 && u <= 0xdbff;
const isTrail = (u: number) => u >= 0xdc00 && u <= 0xdfff;

/** Scalars before `utf16` code units (`TextOffsets.scalarOffset(ofUTF16:in:)`). An offset inside a surrogate pair
 *  counts the pair, i.e. rounds down to the scalar boundary before it; past the end clamps to the scalar count. */
export function scalarOffsetOfUTF16(text: string, utf16: number): number {
  let pairs = 0;
  const limit = Math.min(utf16, text.length);
  let i = 0;
  while (i < limit) {
    if (isLead(text.charCodeAt(i)) && i + 1 < text.length && isTrail(text.charCodeAt(i + 1))) {
      pairs += 1;
      i += 2;
    } else {
      i += 1;
    }
  }
  // Swift returns utf16Offset - pairs even past the end; clamp as a JS caller would expect.
  return Math.min(utf16, text.length) - pairs;
}

/** UTF-16 code units before `scalar` scalars (`TextOffsets.utf16Offset(ofScalar:in:)`); past the end → length. */
export function utf16OffsetOfScalar(text: string, scalar: number): number {
  let utf16 = 0;
  let scalars = 0;
  while (utf16 < text.length && scalars < scalar) {
    const u = text.charCodeAt(utf16);
    utf16 += isLead(u) && utf16 + 1 < text.length && isTrail(text.charCodeAt(utf16 + 1)) ? 2 : 1;
    scalars += 1;
  }
  return utf16;
}

/** Number of Unicode scalars (an unpaired surrogate counts as one, as Swift repairs it to U+FFFD). */
export function scalarLength(text: string): number {
  return scalarOffsetOfUTF16(text, text.length);
}

export function utf16RangeOfScalars(text: string, start: number, end: number): [number, number] {
  return [utf16OffsetOfScalar(text, start), utf16OffsetOfScalar(text, end)];
}

/** `text` sliced by scalar positions, clamped like `FragmentDocument.addComment`. */
export function sliceScalars(text: string, start: number, end: number): string {
  const [a, b] = utf16RangeOfScalars(text, start, end);
  return text.slice(a, Math.max(a, b));
}

/** Many UTF-16 offsets to scalars in one pass (`TextOffsets.scalarRanges`), for highlighting many ranges. */
export function scalarOffsetsOfUTF16(text: string, offsets: number[]): number[] {
  const sorted = [...new Set(offsets)].sort((a, b) => a - b);
  const map = new Map<number, number>();
  let utf16 = 0, scalars = 0, next = 0;
  while (utf16 < text.length) {
    while (next < sorted.length && sorted[next] <= utf16) map.set(sorted[next++], scalars);
    const u = text.charCodeAt(utf16);
    utf16 += isLead(u) && utf16 + 1 < text.length && isTrail(text.charCodeAt(utf16 + 1)) ? 2 : 1;
    scalars += 1;
  }
  while (next < sorted.length) map.set(sorted[next++], scalars);
  return offsets.map((o) => map.get(o) ?? 0);
}
